import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/store/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ChevronRight, SlidersHorizontal, X, ChevronLeft, ChevronRight as ChevronR } from "lucide-react";
import Breadcrumbs from "@/components/store/Breadcrumbs";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "alpha", label: "A → Z" },
  { value: "popular", label: "Most popular" },
  { value: "rating", label: "Top rated" },
];

/**
 * SEO-friendly category landing page.
 * URL pattern: /books/<slug>/<slug>/...
 * Uses /api/categories/by-path/... for tree + /api/catalog/filter for the grid.
 */
export default function CategoryLanding() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const pathSegments = useMemo(() => {
    // strip "/books" prefix, split
    const clean = location.pathname.replace(/^\/books\/?/, "").replace(/\/$/, "");
    return clean ? clean.split("/").filter(Boolean) : [];
  }, [location.pathname]);

  const [categoryData, setCategoryData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({});
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // read filters from URL
  const page = parseInt(params.get("page") || "1", 10);
  const sort = params.get("sort") || "newest";
  const q = params.get("q") || "";
  const priceMin = params.get("price_min") || "";
  const priceMax = params.get("price_max") || "";
  const inStock = params.get("in_stock") === "1";
  const selAuthors = params.getAll("author");
  const selPublishers = params.getAll("publisher");
  const selLanguages = params.getAll("language");
  const selTags = params.getAll("tag");
  const selFormats = params.getAll("format");
  const audience = params.get("audience") || "";
  const readingLevel = params.get("reading_level") || "";

  const updateParams = (updates) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(updates)) {
      if (v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) {
        p.delete(k);
      } else if (Array.isArray(v)) {
        p.delete(k);
        v.forEach((val) => p.append(k, val));
      } else {
        p.set(k, String(v));
      }
    }
    // reset page unless explicitly setting
    if (!("page" in updates)) p.delete("page");
    setParams(p);
  };

  const toggleFacet = (key, value) => {
    const current = params.getAll(key);
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateParams({ [key]: next });
  };

  // Resolve category
  useEffect(() => {
    setLoading(true);
    setError(null);
    if (pathSegments.length === 0) {
      setCategoryData({
        category: { name: "All books", slug: "", path: [], seo_title: "All books | Blessings Connection", meta_description: "Browse every title in our catalog.", book_count: 0 },
        ancestors: [],
        children: [],
      });
      return;
    }
    api.get(`/categories/by-path/${pathSegments.join("/")}`)
      .then((r) => setCategoryData(r.data))
      .catch(() => setError("Category not found"));
  }, [location.pathname]);

  // Load books
  useEffect(() => {
    if (!categoryData) return;
    // Guard against a race when navigating from /books/<slug> → /books?featured=1:
    // React re-runs this effect on params change BEFORE the first effect resolves
    // the new categoryData, so we'd fire a stale request with the old category_slug.
    // Only fetch when categoryData corresponds to the current URL.
    const catPath = (categoryData.category.path || (categoryData.category.slug ? [categoryData.category.slug] : [])).join("/");
    const currentPath = pathSegments.join("/");
    if (catPath !== currentPath) return;

    setLoading(true);
    const filter = {
      page,
      page_size: 24,
      sort,
    };
    if (categoryData.category.slug) filter.category_slug = categoryData.category.slug;
    if (q) filter.q = q;
    if (priceMin) filter.price_min = priceMin;
    if (priceMax) filter.price_max = priceMax;
    if (inStock) filter.in_stock = true;
    if (params.get("featured") === "1") filter.featured = true;
    if (selAuthors.length) filter.authors = selAuthors.join(",");
    if (selPublishers.length) filter.publishers = selPublishers.join(",");
    if (selLanguages.length) filter.language = selLanguages[0]; // API supports single language
    if (selTags.length) filter.tags = selTags.join(",");
    if (selFormats.length) filter.format = selFormats[0];
    if (audience) filter.audience = audience;
    if (readingLevel) filter.reading_level = readingLevel;

    api.get("/catalog/filter", { params: filter })
      .then(async (r) => {
        let respItems = r.data.items || [];
        let tot = r.data.total || 0;
        let pgs = r.data.pages || 0;
        const isPureFeaturedPage = filter.featured && !categoryData.category.slug &&
          !q && !priceMin && !priceMax && !inStock &&
          !selAuthors.length && !selPublishers.length && !selLanguages.length &&
          !selTags.length && !selFormats.length && !audience && !readingLevel;
        // Featured page with no manually-featured books: fall back to the
        // rotating in-stock "featured-or-random" selection so it's never empty.
        if (isPureFeaturedPage && tot === 0) {
          try {
            const fr = await api.get("/books/featured-or-random", { params: { limit: 24 } });
            respItems = Array.isArray(fr.data) ? fr.data : [];
            tot = respItems.length;
            pgs = 1;
          } catch (_) { /* keep empty */ }
        }
        setItems(respItems);
        setFacets(r.data.facets || {});
        setTotal(tot);
        setPages(pgs);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [categoryData, location.search]);

  // SEO meta
  useEffect(() => {
    if (!categoryData) return;
    const cat = categoryData.category;
    document.title = cat.seo_title || `${cat.name} Books | Blessings Connection`;
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content = cat.meta_description || `Shop ${cat.name.toLowerCase()} books at Blessings Connection.`;
    // canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + location.pathname;
  }, [categoryData, location.pathname]);

  if (error) {
    return (
      <div className="container-book py-24 text-center" data-testid="category-not-found">
        <h1 className="font-heading text-4xl font-bold text-slate-900">Category not found</h1>
        <p className="mt-3 text-slate-500">The category "{pathSegments.join(" / ")}" doesn't exist.</p>
        <Link to="/shop" className="mt-6 inline-block text-primary underline">Browse all books</Link>
      </div>
    );
  }

  if (!categoryData) {
    return <div className="container-book py-24 text-center text-slate-400" data-testid="category-loading">Loading…</div>;
  }

  const { category, ancestors, children } = categoryData;
  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Books", href: "/books" },
    ...ancestors.map((a) => ({ label: a.name, href: "/books/" + (a.path || [a.slug]).join("/") })),
    ...(category.slug ? [{ label: category.name }] : []),
  ];

  return (
    <div className="container-book py-6 md:py-10" data-testid="category-page" data-slug={category.slug}>
      <Breadcrumbs items={crumbs} />

      {/* Category Hero */}
      <motion.header
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="mt-4 mb-8 pb-6 border-b border-slate-200"
      >
        <h1 className="font-heading font-bold text-3xl md:text-5xl text-slate-900 tracking-tight" data-testid="category-title">
          {params.get("featured") === "1" ? "Featured titles" : (category.name || "All books")}
        </h1>
        {category.description && (
          <p className="mt-3 text-slate-600 max-w-3xl text-base leading-relaxed" data-testid="category-description">
            {category.description}
          </p>
        )}
        <p className="mt-2 text-slate-500 text-sm" data-testid="category-count">
          {loading ? "Loading…" : `${total} ${total === 1 ? "title" : "titles"}`}
        </p>

        {children && children.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="category-subcategories">
            {children.map((c) => (
              <Link
                key={c.id}
                to={`/books/${(c.path || [c.slug]).join("/")}`}
                data-testid={`subcat-${c.slug}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs text-slate-700 hover:border-primary hover:text-primary transition-colors"
              >
                {c.name} <ChevronRight size={12} />
              </Link>
            ))}
          </div>
        )}
      </motion.header>

      {/* Layout */}
      <div className="grid lg:grid-cols-[280px,1fr] gap-8">
        {/* Sidebar (mobile drawer) */}
        <div className="lg:hidden">
          <Button onClick={() => setMobileFiltersOpen(true)} variant="outline" className="rounded-lg"
            data-testid="mobile-filters-toggle">
            <SlidersHorizontal size={16} className="mr-2" /> Filters
          </Button>
        </div>
        <aside
          className={`${mobileFiltersOpen ? "fixed inset-0 z-50 bg-white overflow-y-auto p-6" : "hidden lg:block"} lg:sticky lg:top-32 lg:self-start lg:h-fit space-y-6`}
          data-testid="category-filters"
        >
          <div className="lg:hidden flex items-center justify-between">
            <h2 className="font-heading font-semibold text-xl">Filters</h2>
            <button onClick={() => setMobileFiltersOpen(false)} className="p-2 rounded-lg hover:bg-slate-100"><X size={20} /></button>
          </div>

          <FilterGroup title="Keyword">
            <Input
              value={params.get("q") || ""}
              onChange={(e) => updateParams({ q: e.target.value })}
              placeholder="Search within category…"
              data-testid="filter-q"
              className="h-10"
            />
          </FilterGroup>

          {facets.authors?.length > 0 && (
            <FilterGroup title="Author">
              {facets.authors.slice(0, 10).map((f) => (
                <FacetCheck key={f.value} label={f.value} count={f.count}
                  checked={selAuthors.includes(f.value)}
                  onChange={() => toggleFacet("author", f.value)}
                  testId={`facet-author-${f.value}`} />
              ))}
            </FilterGroup>
          )}
          {facets.publishers?.length > 0 && (
            <FilterGroup title="Publisher">
              {facets.publishers.slice(0, 10).map((f) => (
                <FacetCheck key={f.value} label={f.value} count={f.count}
                  checked={selPublishers.includes(f.value)}
                  onChange={() => toggleFacet("publisher", f.value)}
                  testId={`facet-publisher-${f.value}`} />
              ))}
            </FilterGroup>
          )}
          {facets.formats?.length > 0 && (
            <FilterGroup title="Format">
              {facets.formats.map((f) => (
                <FacetCheck key={f.value} label={f.value} count={f.count}
                  checked={selFormats.includes(f.value)}
                  onChange={() => toggleFacet("format", f.value)}
                  testId={`facet-format-${f.value}`} />
              ))}
            </FilterGroup>
          )}
          {facets.languages?.length > 0 && (
            <FilterGroup title="Language">
              {facets.languages.slice(0, 10).map((f) => (
                <FacetCheck key={f.value} label={f.value} count={f.count}
                  checked={selLanguages.includes(f.value)}
                  onChange={() => toggleFacet("language", f.value)}
                  testId={`facet-lang-${f.value}`} />
              ))}
            </FilterGroup>
          )}
          {facets.tags?.length > 0 && (
            <FilterGroup title="Tags">
              {facets.tags.slice(0, 15).map((f) => (
                <FacetCheck key={f.value} label={f.value} count={f.count}
                  checked={selTags.includes(f.value)}
                  onChange={() => toggleFacet("tag", f.value)}
                  testId={`facet-tag-${f.value}`} />
              ))}
            </FilterGroup>
          )}
          <FilterGroup title="Price (USD)">
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Min" value={priceMin} onChange={(e) => updateParams({ price_min: e.target.value })} className="h-9" data-testid="filter-price-min" />
              <Input type="number" placeholder="Max" value={priceMax} onChange={(e) => updateParams({ price_max: e.target.value })} className="h-9" data-testid="filter-price-max" />
            </div>
          </FilterGroup>
          <FilterGroup title="Availability">
            <FacetCheck label="In stock only" count={null} checked={inStock}
              onChange={() => updateParams({ in_stock: inStock ? "" : "1" })} testId="filter-in-stock" />
          </FilterGroup>

          <Button onClick={() => setParams({})} variant="outline"
            data-testid="clear-filters" className="w-full">
            Clear all filters
          </Button>
        </aside>

        {/* Grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-slate-500" data-testid="results-count">
              {loading ? "Loading…" : `${total} results`}
            </span>
            <select
              value={sort} onChange={(e) => updateParams({ sort: e.target.value })}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm"
              data-testid="sort-select"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[2/3] bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="py-24 text-center" data-testid="empty-results">
              <p className="font-heading text-2xl font-semibold text-slate-900">No books match your filters.</p>
              <p className="text-slate-500 mt-2 text-sm">Try clearing filters or search a different term.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="category-grid">
              {items.map((b) => <ProductCard key={b.id} book={b} />)}
            </div>
          )}

          {pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2" data-testid="pagination">
              <button
                disabled={page <= 1}
                onClick={() => updateParams({ page: page - 1 })}
                data-testid="page-prev"
                className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-200 disabled:opacity-40 hover:border-primary hover:text-primary text-sm"
              ><ChevronLeft size={14} /> Prev</button>
              <span className="text-sm text-slate-600 px-3" data-testid="page-info">Page {page} of {pages}</span>
              <button
                disabled={page >= pages}
                onClick={() => updateParams({ page: page + 1 })}
                data-testid="page-next"
                className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-200 disabled:opacity-40 hover:border-primary hover:text-primary text-sm"
              >Next <ChevronR size={14} /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-slate-500 tracking-wider mb-2">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function FacetCheck({ label, count, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={onChange}
        data-testid={testId}
        className="rounded border-slate-300 text-primary focus:ring-primary" />
      <span className="flex-1 truncate">{label}</span>
      {count !== null && count !== undefined && <span className="text-slate-400 text-xs">({count})</span>}
    </label>
  );
}
